import { BuilderContent } from '@builder.io/sdk';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import Icon, { IconType } from '@/components/Icon';
import { LOCALE_LINKS, useLocale } from '@/helpers/LangHelper';
import { FooterMatrix } from '@/helpers/NavHelper';
import { useWindowSize } from '@/hooks/useWindowSize';
import { ChunkedNav } from '@/types/Navigation';

import Auth from './Auth';
import MobileNavigation from './MobileNavigation';
import styles from './navigation.module.scss';
import SearchBar from './SearchBar';
import TopLevelNavigation from './TopLevelNavigation';

export interface Props {
  navigationItems: ChunkedNav;
  footerItems: FooterMatrix[];
  builderNavLinks?: Array<BuilderContent> | null;
}

export const Navigation: React.FC<Props> = ({ navigationItems, footerItems, builderNavLinks }) => {
  const [isMobileMenuActive, setIsMobileMenuActive] = useState(false);
  const [needCloseMobileMenu, setNeedCloseMobileMenu] = useState(false);
  const { locale } = useLocale();
  const { width = 0 } = useWindowSize();

  useEffect(() => {
    if (width < 1024) {
      if (needCloseMobileMenu) {
        setNeedCloseMobileMenu(false);
        setIsMobileMenuActive(false);
      }
    } else {
      setNeedCloseMobileMenu(true);
      if (isMobileMenuActive) {
        setIsMobileMenuActive(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  return (
    <div className={styles.globalHeader}>
      <div className={styles.navBorder}>
        <div className={styles.navContainer}>
          <div className={styles.mobileNavigationContainer}>
            <div
              className={classNames(styles.hamburgerLines, { [styles.active]: isMobileMenuActive })}
              onClick={() => setIsMobileMenuActive((val) => !val)}
            >
              <span className={classNames(styles.line, styles.line1)}></span>
              <span className={classNames(styles.line, styles.line2)}></span>
              <span className={classNames(styles.line, styles.line3)}></span>
            </div>
          </div>
          <div className={styles.navLogoContainer}>
            <a className={styles.navLogo} href={LOCALE_LINKS[locale]?.home}>
              <Icon type={IconType.Afterpay} />
            </a>

            <div className={styles.navLinksContainer}>
              {builderNavLinks
                ? builderNavLinks.map(({ data }, index) => {
                    if (data?.link?.url && data?.link?.text) {
                      return (
                        <a
                          className={styles.navToplevelItemLink}
                          key={index}
                          role="button"
                          target={!data.link.newTab ? undefined : '_blank'}
                          href={data.link.url}
                          rel="noopener noreferrer"
                        >
                          {data.link.text}
                        </a>
                      );
                    }
                  })
                : null}
            </div>
          </div>
          <SearchBar />
          <Auth />
        </div>

        <TopLevelNavigation navigationItems={navigationItems} />

        <MobileNavigation
          navigationItems={navigationItems}
          footerItems={footerItems}
          isMobileMenuActive={isMobileMenuActive}
        />
      </div>
    </div>
  );
};
